// useChunkUploadQueue — upload-машинерия записи лекции: одна попытка
// (register → presigned PUT → confirm), retry-цепочка, persistent очередь
// недоставленных чанков (idb-keyval, lib/recording-upload-queue), finalize
// с ожиданием очереди и deferred auto-finalize.
//
// Выделено из use-recorder.ts (2026-07-28, лимит 500 строк): use-recorder
// держит жизненный цикл записи (start/pause/resume/stop/cancel), этот хук —
// доставку аудио и завершение записи.
//
// Инварианты:
//   - Чанк НИКОГДА не теряется молча: после MAX_CHUNK_RETRY — в persistent
//     очередь; permanent-отказ сервера — drop + toast (явная потеря).
//   - finalize возможен только когда и in-flight ретраи завершились, и
//     persistent-очередь пуста (после finalize сервер чанки НЕ принимает —
//     uploadChunk status-guard). Иначе — deferred finalize: запись остаётся
//     открытой, auto-finalize из maybeAutoFinalize.

import type { AudioChunkEvent } from "@studyhub/platform/ports/audio.port";
import { toast } from "@studyhub/ui/toast";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useCurrentUser } from "@/hooks/use-session";
import i18n from "@/i18n";
import { logClientError } from "@/lib/client-log";
import { setPendingSyncCount } from "@/lib/pending-sync-count";
import { loadPendingFinalizeIds, removePendingFinalizeId } from "@/lib/recording-pending-finalize";
import { type ChunkUploadQueue, createChunkUploadQueue } from "@/lib/recording-upload-queue";
import { api } from "@/lib/trpc-client";
import { useRecorderStore } from "@/stores/recorder";

/** Длительность одного аудио-чанка записи (сек) — окно STT и ordering. */
export const CHUNK_SEC = 10;
const MAX_CHUNK_RETRY = 3;

function mimeTypeToFormat(mime: string): "opus" | "mp3" | "wav" | "aac" {
	if (mime.includes("wav")) return "wav";
	if (mime.includes("opus") || mime.includes("webm") || mime.includes("ogg")) return "opus";
	if (mime.includes("mp3") || mime.includes("mpeg")) return "mp3";
	if (mime.includes("aac") || mime.includes("mp4")) return "aac";
	return "opus";
}

/**
 * Технические параметры chunk'а для метаданных upload-mutation. Зависят от
 * выходного формата платформенного recorder'а: web (audio.web.ts) пишет
 * PCM 16-bit mono 16kHz WAV; Capacitor native — AAC/Opus с другим bitrate.
 */
function chunkAudioParams(mime: string): { bitrate: number; sampleRate: number } {
	if (mime.includes("wav")) return { bitrate: 256_000, sampleRate: 16_000 };
	// Capacitor native fallback (opus/aac @ ~32kbps mono speech).
	return { bitrate: 32_000, sampleRate: 48_000 };
}

/**
 * Серверные ответы, после которых повторная доставка чанка бессмысленна:
 * запись финализирована (status-guard uploadChunk) или удалена вместе с чанками.
 * Точные тексты — modules/recordings/mutations/{upload-chunk,confirm-chunk}.ts.
 */
const PERMANENT_CHUNK_ERROR = /Cannot upload chunk|Recording not found|Chunk not found/;
function isPermanentChunkError(err: unknown): boolean {
	return err instanceof Error && PERMANENT_CHUNK_ERROR.test(err.message);
}

export interface ChunkUploadQueueHandle {
	/** Persistent очередь недоставленных чанков (drain/drop/count). */
	queue: ChunkUploadQueue;
	/** Точка входа аплоада чанка: retry-цепочка + учёт in-flight. */
	uploadOneChunk: (recId: string, event: AudioChunkEvent) => Promise<void>;
	/** Finalize recordings.finalize (идемпотентен к повтору). */
	finalizeRecording: (recId: string) => Promise<{ recordingId: string } | null>;
	/** Записи, чей finalize ждёт опустошения upload-очереди (deferred finalize). */
	pendingFinalizeRef: React.RefObject<Set<string>>;
	/** In-flight аплоады (retry-цепочки) — stop() обязан их дождаться. */
	inFlightUploadsRef: React.RefObject<Set<Promise<void>>>;
	/**
	 * Записи с чанком, который не удалось ни залить, ни положить в IDB.
	 * Блокирует auto/stop finalize (иначе запись соберётся без чанка).
	 * Снимается только явным forceFinalize.
	 */
	undeliverableRecordingsRef: React.RefObject<Set<string>>;
}

export function useChunkUploadQueue({
	chunkCountRef,
}: {
	/** Счётчик сгенерированных чанков (expectedChunks для finalize). */
	chunkCountRef: React.RefObject<number>;
}): ChunkUploadQueueHandle {
	// Владелец очереди: после смены аккаунта чужие чанки не дренятся, а очередь
	// пересоздаётся (см. lib/idb-outbox.ts).
	const userId = useCurrentUser()?.id;
	const incrementChunksUploaded = useRecorderStore((s) => s.incrementChunksUploaded);
	const setChunksPending = useRecorderStore((s) => s.setChunksPending);
	const setChunksTotal = useRecorderStore((s) => s.setChunksTotal);
	const setStatus = useRecorderStore((s) => s.setStatus);
	const setError = useRecorderStore((s) => s.setError);

	const uploadChunkMutation = api.recordings.uploadChunk.useMutation();
	const confirmChunkMutation = api.recordings.confirmChunk.useMutation();
	const finalizeMutation = api.recordings.finalize.useMutation();

	/** Записи, чей finalize ждёт опустошения upload-очереди (deferred finalize). */
	const pendingFinalizeRef = useRef<Set<string>>(new Set());
	/**
	 * In-flight аплоады чанков (retry-цепочка uploadOneChunk). Они ещё НЕ в
	 * persistent-очереди: stop() обязан дождаться их до finalize — иначе
	 * аплоад упрётся в status-guard сервера и чанк будет отброшен (явная,
	 * но потеря). Завершение последнего in-flight при deferred finalize
	 * триггерит auto-finalize (maybeAutoFinalize).
	 */
	const inFlightUploadsRef = useRef<Set<Promise<void>>>(new Set());
	/** Чанк потерян (upload fail + IDB enqueue fail) — не финализировать молча. */
	const undeliverableRecordingsRef = useRef<Set<string>>(new Set());

	/** Одна попытка доставки чанка: register → presigned PUT → confirm. */
	const attemptChunkUpload = useCallback(
		async (recId: string, event: AudioChunkEvent): Promise<void> => {
			const startSec = event.sequence * CHUNK_SEC;
			const endSec = (event.sequence + 1) * CHUNK_SEC;
			const params = chunkAudioParams(event.mimeType);
			const upload = await uploadChunkMutation.mutateAsync({
				recordingId: recId,
				sequence: event.sequence,
				startSec,
				endSec,
				sizeBytes: event.blob.size,
				format: mimeTypeToFormat(event.mimeType),
				bitrate: params.bitrate,
				sampleRate: params.sampleRate,
			});

			const putResp = await fetch(upload.presignedUrl, {
				method: "PUT",
				body: event.blob,
				headers: { "Content-Type": event.mimeType },
			});
			if (!putResp.ok) {
				throw new Error(`S3 PUT failed: ${putResp.status} ${putResp.statusText}`);
			}
			const s3Etag = putResp.headers.get("etag")?.replace(/"/g, "") ?? "";
			await confirmChunkMutation.mutateAsync({
				chunkId: upload.chunk.id,
				s3Etag,
			});
			incrementChunksUploaded();
		},
		[uploadChunkMutation, confirmChunkMutation, incrementChunksUploaded],
	);

	// Ref-обёртки разрывают циклические зависимостей колбэков очереди: mutation
	// объекты react-query меняют identity каждый рендер, а очередь должна быть
	// синглтоном (иначе background-listener'ы пересоздавались бы на рендер).
	const uploadRef = useRef(attemptChunkUpload);
	useEffect(() => {
		uploadRef.current = attemptChunkUpload;
	}, [attemptChunkUpload]);

	const finalizeRecording = useCallback(
		async (recId: string): Promise<{ recordingId: string } | null> => {
			if (undeliverableRecordingsRef.current.has(recId)) {
				setError(i18n.t("recorder.queue.undeliverable", { ns: "recordings" }));
				return null;
			}
			// Finalize — Inngest processRecording возьмёт работу.
			try {
				const finalized = await finalizeMutation.mutateAsync({
					recordingId: recId,
					expectedChunks: chunkCountRef.current,
				});
				setChunksTotal(finalized.recording.chunksTotal);
				setStatus("processing");
				pendingFinalizeRef.current.delete(recId);
				void removePendingFinalizeId(recId);
				return { recordingId: recId };
			} catch (err) {
				// Идемпотентность: повторный finalize (гонка stop/force/auto) — успех.
				if (err instanceof Error && err.message.includes("already finalized")) {
					setStatus("processing");
					pendingFinalizeRef.current.delete(recId);
					void removePendingFinalizeId(recId);
					return { recordingId: recId };
				}
				logClientError("useRecorder: finalize failed", err, { recordingId: recId });
				setError(
					err instanceof Error
						? err.message
						: i18n.t("recorder.finalizeFailed", { ns: "recordings" }),
				);
				return null;
			}
		},
		[finalizeMutation, chunkCountRef, setChunksTotal, setStatus, setError],
	);

	const finalizeRef = useRef(finalizeRecording);
	useEffect(() => {
		finalizeRef.current = finalizeRecording;
	}, [finalizeRecording]);

	// Ref на maybeAutoFinalize — очередь (useMemo ниже) вызывает его из
	// onQueueEmptied, а он сам зависит от queue → цикл разрывается ref'ом.
	const maybeAutoFinalizeRef = useRef<(recId: string) => Promise<void>>(async () => {});

	const queue: ChunkUploadQueue = useMemo(
		() =>
			createChunkUploadQueue({
				userId,
				upload: (chunk) => uploadRef.current(chunk.recordingId, chunk),
				isPermanentError: isPermanentChunkError,
				onCountChange: (count) => {
					setChunksPending(count);
					// OfflineBanner: очередь чанков записи + tutor outbox.
					setPendingSyncCount("recording", count);
				},
				onDropped: (dropped) =>
					toast.error(i18n.t("recorder.queue.dropped", { ns: "recordings", count: dropped })),
				onQueueEmptied: (recId) => {
					// Deferred finalize: stop() ждал очередь — она опустела, завершаем.
					void maybeAutoFinalizeRef.current(recId);
				},
			}),
		[setChunksPending, userId],
	);

	/**
	 * Auto-finalize отложенной записи: stop() вернул null (офлайн/зависшие
	 * in-flight), а теперь и очередь пуста, и in-flight завершились.
	 * Вызывается из onQueueEmptied (drain) и из finally uploadOneChunk
	 * (успешный in-flight при пустой очереди не порождает drain-события).
	 */
	const maybeAutoFinalize = useCallback(
		async (recId: string): Promise<void> => {
			if (!pendingFinalizeRef.current.has(recId)) return;
			if (undeliverableRecordingsRef.current.has(recId)) return;
			if (inFlightUploadsRef.current.size > 0) return;
			if ((await queue.countForRecording(recId)) > 0) return;
			pendingFinalizeRef.current.delete(recId);
			void removePendingFinalizeId(recId);
			toast.success(i18n.t("recorder.queue.allUploaded", { ns: "recordings" }));
			void finalizeRef.current(recId);
		},
		[queue],
	);
	useEffect(() => {
		maybeAutoFinalizeRef.current = maybeAutoFinalize;
	}, [maybeAutoFinalize]);

	// Recovery после reload: восстановить deferred-finalize из IDB и добить очередь.
	useEffect(() => {
		let cancelled = false;
		void loadPendingFinalizeIds().then((ids) => {
			if (cancelled) return;
			for (const id of ids) pendingFinalizeRef.current.add(id);
			for (const id of ids) void maybeAutoFinalizeRef.current(id);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	/** Retry-цепочка одного чанка (рекурсивная, без tracking — см. обёртку). */
	const uploadOneChunkWithRetry = useCallback(
		async (recId: string, event: AudioChunkEvent, attempt = 1): Promise<void> => {
			try {
				await attemptChunkUpload(recId, event);
			} catch (err) {
				if (attempt >= MAX_CHUNK_RETRY) {
					logClientError("useRecorder: chunk upload failed, queued for background delivery", err, {
						recordingId: recId,
						sequence: event.sequence,
					});
					try {
						await queue.enqueue({
							recordingId: recId,
							sequence: event.sequence,
							mimeType: event.mimeType,
							blob: event.blob,
							enqueuedAt: Date.now(),
						});
						void queue.drain();
					} catch (queueErr) {
						logClientError("useRecorder: persistent queue unavailable, chunk lost", queueErr, {
							recordingId: recId,
							sequence: event.sequence,
						});
						undeliverableRecordingsRef.current.add(recId);
						toast.error(i18n.t("recorder.queue.undeliverable", { ns: "recordings" }));
						// Не резолвить: иначе stop() при пустой очереди финализирует
						// запись без чанка.
						throw queueErr instanceof Error
							? queueErr
							: new Error("Persistent upload queue unavailable");
					}
					return;
				}
				// Backoff 1s × attempt, потом retry.
				await new Promise<void>((r) => setTimeout(r, 1000 * attempt));
				await uploadOneChunkWithRetry(recId, event, attempt + 1);
			}
		},
		[attemptChunkUpload, queue],
	);

	/** Точка входа аплоада чанка: retry + учёт in-flight для stop()/auto-finalize. */
	const uploadOneChunk = useCallback(
		async (recId: string, event: AudioChunkEvent): Promise<void> => {
			const p = uploadOneChunkWithRetry(recId, event);
			inFlightUploadsRef.current.add(p);
			try {
				await p;
			} finally {
				inFlightUploadsRef.current.delete(p);
				// Последний in-flight при deferred finalize: очередь может быть
				// уже пуста (успешная доставка) — drain-события не будет.
				void maybeAutoFinalizeRef.current(recId);
			}
		},
		[uploadOneChunkWithRetry],
	);

	return {
		queue,
		uploadOneChunk,
		finalizeRecording,
		pendingFinalizeRef,
		inFlightUploadsRef,
		undeliverableRecordingsRef,
	};
}
